import { useEffect, useState } from 'react'
import { api } from '../Services/api'
import { track } from '../Utils/analytics'
import { KEYS, readJSON, writeJSON } from '../Utils/storage'
import { norm } from '../Utils/text'

export function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const results = new Map() // normalized query -> response, for this visit

/* search_query (analytics spec 5.3): once per query, when it's submitted or a
   result is opened, never per letter. Only the length goes out. */
let loggedQuery = ''
export function logSearchQuery(query, data) {
  const text = String(query || '').trim()
  if (text.length < 2 || norm(text) === loggedQuery) return
  loggedQuery = norm(text)
  const counts = {
    topics: data?.topics?.length ?? 0,
    stories: data?.stories?.length ?? 0,
    sources: data?.sources?.length ?? 0,
    categories: data?.categories?.length ?? 0,
  }
  track('search_query', {
    query_length: text.length,
    has_results: Object.values(counts).some((n) => n > 0),
    result_counts: counts,
  })
}

// A search result opened (and with it the query, if not yet).
export function logSearchResult(query, data, type, position) {
  logSearchQuery(query, data)
  track('search_result_open', { type, position })
}

/* Topics, categories, sources and the first stories for a query, while typing. */
export function useSearch(query, delay = 200) {
  const q = useDebounced(query.trim(), delay)
  const key = norm(q)
  const [state, setState] = useState({ key: '', data: null, loading: false, error: null })

  useEffect(() => {
    if (key.length < 2) {
      setState({ key, data: null, loading: false, error: null })
      return undefined
    }
    if (results.has(key)) {
      setState({ key, data: results.get(key), loading: false, error: null })
      return undefined
    }
    let alive = true
    setState((s) => ({ ...s, key, loading: true, error: null }))
    api
      .search(q)
      .then((data) => {
        results.set(key, data)
        if (alive) setState({ key, data, loading: false, error: null })
      })
      .catch((error) => alive && setState({ key, data: null, loading: false, error }))
    return () => {
      alive = false
    }
  }, [key, q])

  const fresh = state.key === key
  return {
    query: q,
    data: fresh ? state.data : null,
    loading: key.length >= 2 && (!fresh || state.loading),
    error: fresh ? state.error : null,
  }
}

/* Mobile Istraži: the last searches, newest first (spec 7.3). */
const MAX_RECENT = 8

export function readRecentSearches() {
  const list = readJSON(KEYS.recentSearches, [])
  return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : []
}

export function saveRecentSearch(query) {
  const text = String(query || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length < 2) return readRecentSearches()
  const next = [text, ...readRecentSearches().filter((s) => norm(s) !== norm(text))].slice(
    0,
    MAX_RECENT
  )
  writeJSON(KEYS.recentSearches, next)
  return next
}

export function removeRecentSearch(query) {
  const next = readRecentSearches().filter((s) => s !== query)
  writeJSON(KEYS.recentSearches, next)
  return next
}

export function clearRecentSearches() {
  writeJSON(KEYS.recentSearches, [])
  return []
}
